import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import {
  types,
  Kiosk,
  KioskSchedule,
  GetKiosksRequest,
  GetKioskRequest,
  GetKioskScheduleRequest,
  UpdateKioskScheduleRequest,
  UpdateKioskRequest,
  UpdateKiosksRequest,
  RebootKioskRequest,
  ChangeStatusKioskRequest,
  LogEventRequest,
  CreateKioskRequest,
  DeleteKioskRequest,
  KioskLoginTokenRequest
} from './types';
import { API, AdminAPI } from 'services/api/base';
import { getErrorCode } from 'services/api/normalizer';
import { selectors as clientSelectors } from 'store/clients';

export function* getKiosksAsync({ payload }: GetKiosksRequest) {
  const { exportData = false } = payload;
  yield put({ type: types.GET_KIOSKS.PENDING, payload: { pending: true, exportData } });

  try {
    const apiPayload = exportData ? { includeSchedules: true } : {};
    const clientId: string = yield select(clientSelectors.selectClientId);

    const api = new API(`clients/${clientId}/kiosks`);
    const response: Kiosk[] = yield call(api.getAll, apiPayload);

    yield put({ type: types.GET_KIOSKS.SUCCESS, payload: { kiosks: response, exportData } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_KIOSKS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_KIOSKS.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_KIOSKS.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_KIOSKS.PENDING, payload: { pending: false, exportData } });
  }
}

export function* getKioskAsync({ payload }: GetKioskRequest) {
  yield put({ type: types.GET_KIOSK.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks`);
    const response: Kiosk[] = yield call(api.getOne, kioskId);

    yield put({ type: types.GET_KIOSK.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_KIOSK.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_KIOSK.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_KIOSK.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_KIOSK.PENDING, payload: false });
  }
}

export function* createKioskAsync({ payload }: CreateKioskRequest) {
  yield put({ type: types.CREATE_KIOSK.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_KIOSK.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_KIOSK.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_KIOSK.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_KIOSK.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_KIOSK.PENDING, payload: false });
  }
}

export function* updateKioskAsync({ payload }: UpdateKioskRequest) {
  yield put({ type: types.UPDATE_KIOSK.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks`);
    const response: Kiosk = yield call(api.update, payload);

    yield put({ type: types.UPDATE_KIOSK.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_KIOSK.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_KIOSK.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_KIOSK.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_KIOSK.PENDING, payload: false });
  }
}

export function* updateKiosksAsync({ payload }: UpdateKiosksRequest) {
  yield put({ type: types.UPDATE_KIOSKS.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/updateKiosks`);
    const response: { kiosks: Kiosk[] } = yield call(api.create, payload);

    yield put({ type: types.UPDATE_KIOSKS.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_KIOSKS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_KIOSKS.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_KIOSKS.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_KIOSKS.PENDING, payload: false });
  }
}

export function* deleteKioskAsync({ payload }: DeleteKioskRequest) {
  yield put({ type: types.DELETE_KIOSK.PENDING, payload: true });

  try {
    const { id } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_KIOSK.SUCCESS, payload: { id } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_KIOSK.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_KIOSK.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_KIOSK.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_KIOSK.PENDING, payload: false });
  }
}

export function* getKioskScheduleAsync({ payload }: GetKioskScheduleRequest) {
  yield put({ type: types.GET_KIOSK_SCHEDULE.PENDING, payload: true });

  try {
    const { kioskId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/schedule`);
    const { schedule: response }: { schedule: KioskSchedule } = yield call(api.getAll);

    yield put({ type: types.GET_KIOSK_SCHEDULE.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_KIOSK_SCHEDULE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_KIOSK_SCHEDULE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_KIOSK_SCHEDULE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_KIOSK_SCHEDULE.PENDING, payload: false });
  }
}

export function* updateKioskScheduleAsync({ payload }: UpdateKioskScheduleRequest) {
  yield put({ type: types.UPDATE_KIOSK_SCHEDULE.PENDING, payload: true });

  try {
    const { kioskId, schedule } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/schedule`);
    const { schedule: response }: { schedule: KioskSchedule } = yield call(api.create, schedule);

    yield put({ type: types.UPDATE_KIOSK_SCHEDULE.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.UPDATE_KIOSK_SCHEDULE.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_KIOSK_SCHEDULE.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_KIOSK_SCHEDULE.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_KIOSK_SCHEDULE.PENDING, payload: false });
  }
}

export function* rebootKioskAsync({ payload }: RebootKioskRequest) {
  yield put({ type: types.REBOOT_KIOSK.PENDING, payload: true });

  try {
    const { kioskId, adminId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/reboot`);
    yield call(api.create, { actorId: adminId, event: 'Kiosk Rebooted', category: 'reboot' });

    yield put({ type: types.REBOOT_KIOSK.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.REBOOT_KIOSK.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.REBOOT_KIOSK.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.REBOOT_KIOSK.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.REBOOT_KIOSK.PENDING, payload: false });
  }
}

export function* changeStatusKioskAsync({ payload }: ChangeStatusKioskRequest) {
  yield put({ type: types.CHANGE_STATUS_KIOSK.PENDING, payload: true });

  try {
    const { kioskId, status, adminId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);

    let statusChangeData;
    if (status === 'Active') {
      statusChangeData = { actorId: adminId, event: 'Kiosk Active', category: 'active_status' };
    } else {
      statusChangeData = { actorId: adminId, event: 'Kiosk Inactive', category: 'inactive_status' };
    }

    const api = new API(`clients/${clientId}/kiosks/${kioskId}/changestatus`);
    yield call(api.create, statusChangeData);

    yield put({ type: types.CHANGE_STATUS_KIOSK.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CHANGE_STATUS_KIOSK.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CHANGE_STATUS_KIOSK.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CHANGE_STATUS_KIOSK.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CHANGE_STATUS_KIOSK.PENDING, payload: false });
  }
}

export function* checkAppVersionAsync() {
  yield put({ type: types.CHECK_APP_VERSION.PENDING });

  try {
    const api = new AdminAPI(`/meta.json?${new Date().getTime()}`);
    const response: { version: string } = yield call(api.getAll);

    if (response && response.version) {
      yield put({ type: types.CHECK_APP_VERSION.SUCCESS, payload: response.version });
    }
  } catch (err) {
    // App version check Failure
  }
}

// log events
export function* logKioskEventAsync({ payload }: LogEventRequest) {
  yield put({ type: types.LOG_EVENT.PENDING, payload: true });

  try {
    const { kioskId, adminId } = payload;
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/kiosks/${kioskId}/events`);
    // TODO: why is this specifically for Ticket Stock Reloaded?
    yield call(api.create, { actorId: adminId, event: 'Ticket Stock Reloaded', category: 'ticket_stock_reload' });

    yield put({ type: types.LOG_EVENT.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.LOG_EVENT.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.LOG_EVENT.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.LOG_EVENT.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.LOG_EVENT.PENDING, payload: false });
  }
}

export function* createKioskLoginTokenAsync({ payload }: KioskLoginTokenRequest) {
  const { kioskId, adminId } = payload;
  yield put({ type: types.KIOSK_LOGIN_TOKEN.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const kioskLoginApi = new API(`clients/${clientId}/kiosks/${kioskId}/loginToken`);

    const { token } = yield call(kioskLoginApi.create, { adminId });

    yield put({ type: types.KIOSK_LOGIN_TOKEN.SUCCESS, payload: { [kioskId]: token } });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.KIOSK_LOGIN_TOKEN.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.KIOSK_LOGIN_TOKEN.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.KIOSK_LOGIN_TOKEN.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.KIOSK_LOGIN_TOKEN.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_KIOSKS.REQUEST, getKiosksAsync),
  takeLatest(types.GET_KIOSK.REQUEST, getKioskAsync),
  takeLatest(types.CREATE_KIOSK.REQUEST, createKioskAsync),
  takeLatest(types.UPDATE_KIOSK.REQUEST, updateKioskAsync),
  takeLatest(types.UPDATE_KIOSKS.REQUEST, updateKiosksAsync),
  takeLatest(types.DELETE_KIOSK.REQUEST, deleteKioskAsync),
  takeLatest(types.GET_KIOSK_SCHEDULE.REQUEST, getKioskScheduleAsync),
  takeLatest(types.UPDATE_KIOSK_SCHEDULE.REQUEST, updateKioskScheduleAsync),
  takeLatest(types.REBOOT_KIOSK.REQUEST, rebootKioskAsync),
  takeLatest(types.CHANGE_STATUS_KIOSK.REQUEST, changeStatusKioskAsync),
  takeLatest(types.CHECK_APP_VERSION.REQUEST, checkAppVersionAsync),
  takeLatest(types.LOG_EVENT.REQUEST, logKioskEventAsync),
  takeLatest(types.KIOSK_LOGIN_TOKEN.REQUEST, createKioskLoginTokenAsync)
];
